type ExchangeRateApiEnvelope = {
  data?: {
    baseCurrency?: string;
    rates?: Record<string, number>;
  };
};

export type ExchangeRatesByBase = Record<string, Record<string, number>>;
export type DailyExchangeRatesByDate = Record<string, ExchangeRatesByBase>;

export const normalizeCurrencyCode = (value: unknown): string => {
  if (typeof value !== "string") {
    return "TRY";
  }

  const normalized = value.trim().toUpperCase();
  return normalized || "TRY";
};

export const extractExchangeRates = (
  payload: ExchangeRateApiEnvelope | null | undefined,
): { baseCurrency: string; rates: Record<string, number> } | null => {
  const baseCurrency = normalizeCurrencyCode(payload?.data?.baseCurrency);
  const rawRates = payload?.data?.rates;

  if (!rawRates || typeof rawRates !== "object") {
    return null;
  }

  const rates = Object.entries(rawRates).reduce<Record<string, number>>(
    (acc, [currency, value]) => {
      if (typeof value === "number" && Number.isFinite(value) && value > 0) {
        acc[normalizeCurrencyCode(currency)] = value;
      }
      return acc;
    },
    {},
  );

  rates[baseCurrency] = 1;

  return {
    baseCurrency,
    rates,
  };
};

export const getConversionRate = (
  exchangeRatesByBase: ExchangeRatesByBase,
  fromCurrency: string,
  toCurrency: string,
): number | null => {
  const from = normalizeCurrencyCode(fromCurrency);
  const to = normalizeCurrencyCode(toCurrency);

  if (from === to) {
    return 1;
  }

  const directRate = exchangeRatesByBase[from]?.[to];
  if (typeof directRate === "number" && Number.isFinite(directRate) && directRate > 0) {
    return directRate;
  }

  const inverseRate = exchangeRatesByBase[to]?.[from];
  if (
    typeof inverseRate === "number" &&
    Number.isFinite(inverseRate) &&
    inverseRate > 0
  ) {
    return 1 / inverseRate;
  }

  for (const rates of Object.values(exchangeRatesByBase)) {
    const fromRate = rates?.[from];
    const toRate = rates?.[to];
    if (
      typeof fromRate === "number" &&
      Number.isFinite(fromRate) &&
      fromRate > 0 &&
      typeof toRate === "number" &&
      Number.isFinite(toRate) &&
      toRate > 0
    ) {
      return toRate / fromRate;
    }
  }

  return null;
};

export const toCurrencyDateKey = (value: Date | string | null | undefined): string | null => {
  if (!value) {
    return null;
  }

  if (value instanceof Date) {
    if (!Number.isFinite(value.getTime())) {
      return null;
    }
    const year = value.getFullYear();
    const month = String(value.getMonth() + 1).padStart(2, "0");
    const day = String(value.getDate()).padStart(2, "0");
    return `${year}-${month}-${day}`;
  }

  if (typeof value === "string") {
    const trimmed = value.trim();
    if (!trimmed) {
      return null;
    }
    return trimmed.length >= 10 ? trimmed.slice(0, 10) : trimmed;
  }

  return null;
};

export const getConversionRateOnDate = (
  dailyExchangeRatesByDate: DailyExchangeRatesByDate,
  latestExchangeRatesByBase: ExchangeRatesByBase,
  fromCurrency: string,
  toCurrency: string,
  date: Date | string | null | undefined,
): number | null => {
  const dateKey = toCurrencyDateKey(date);
  if (dateKey) {
    const dailyRates = dailyExchangeRatesByDate[dateKey];
    if (dailyRates) {
      const dailyRate = getConversionRate(dailyRates, fromCurrency, toCurrency);
      if (dailyRate !== null) {
        return dailyRate;
      }
    }
  }

  return getConversionRate(latestExchangeRatesByBase, fromCurrency, toCurrency);
};

export const convertCurrencyAmount = (
  amount: number | null | undefined,
  fromCurrency: string,
  toCurrency: string,
  exchangeRatesByBase: ExchangeRatesByBase,
): number | null => {
  if (amount === null || amount === undefined || !Number.isFinite(amount)) {
    return null;
  }

  const rate = getConversionRate(exchangeRatesByBase, fromCurrency, toCurrency);
  if (rate === null) {
    return null;
  }

  return amount * rate;
};

export const convertCurrencyAmountOnDate = (
  amount: number | null | undefined,
  fromCurrency: string,
  toCurrency: string,
  date: Date | string | null | undefined,
  dailyExchangeRatesByDate: DailyExchangeRatesByDate,
  latestExchangeRatesByBase: ExchangeRatesByBase,
): number | null => {
  if (amount === null || amount === undefined || !Number.isFinite(amount)) {
    return null;
  }

  const rate = getConversionRateOnDate(
    dailyExchangeRatesByDate,
    latestExchangeRatesByBase,
    fromCurrency,
    toCurrency,
    date,
  );
  if (rate === null) {
    return null;
  }

  return amount * rate;
};

type CurrencyDisplayMode = "symbol" | "suffix";

type FormatCurrencyByCodeOptions = {
  display?: CurrencyDisplayMode;
};

export const getCurrencySuffixLabel = (currencyCode: string): string => {
  const normalizedCurrency = normalizeCurrencyCode(currencyCode);
  if (normalizedCurrency === "TRY") {
    return "TL";
  }
  return normalizedCurrency;
};

export const formatCurrencyByCode = (
  value: number | null | undefined,
  currencyCode: string,
  options?: FormatCurrencyByCodeOptions,
): string => {
  if (value === null || value === undefined || !Number.isFinite(value)) {
    return "-";
  }

  const normalizedCurrency = normalizeCurrencyCode(currencyCode);
  const display = options?.display || "symbol";

  if (display === "suffix") {
    return `${value.toLocaleString("tr-TR", {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    })} ${getCurrencySuffixLabel(normalizedCurrency)}`;
  }

  try {
    return new Intl.NumberFormat("tr-TR", {
      style: "currency",
      currency: normalizedCurrency,
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(value);
  } catch {
    return `${value.toLocaleString("tr-TR", {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    })} ${normalizedCurrency}`;
  }
};
